import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Crown, ChevronLeft, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;
const GATEWAY_LABEL = { viva: "Viva Wallet", revolut: "Revolut" };

function fmtPrice(value) {
  return Number(value || 0).toFixed(2).replace(".", ",");
}

export default function KaisoPrimePage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [gateway, setGateway] = useState("");
  const [step, setStep] = useState("info");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!restaurantId) return;
    axios.get(`${API}/api/prime/public-info?restaurant_id=${encodeURIComponent(restaurantId)}`)
      .then(r => {
        setInfo(r.data);
        setGateway((r.data?.gateways || [])[0] || "");
      })
      .catch(() => setError("No se pudo cargar la información de PRIME."));
  }, [restaurantId]);

  const planPrice = info?.plan_price ?? 3.99;
  // Benefits come from the backend so the page can never advertise something
  // the system does not actually implement.
  const benefits = info?.benefits || [];
  const gateways = info?.gateways || [];

  const startCheckout = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 9) { setError("Introduce un número de teléfono válido"); return; }
    if (gateways.length === 0) { setError("Este restaurante todavía no acepta pagos PRIME."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/api/prime/checkout`, {
        restaurant_id: restaurantId,
        phone: digits,
        customer_name: name.trim(),
        gateway: gateway || gateways[0],
        return_url: `${window.location.origin}/portal/${restaurantId}`,
      });
      if (!res.data?.checkout_url) throw new Error("missing_checkout_url");
      try {
        localStorage.setItem(`prime_intent_${restaurantId}`, res.data.intent_id);
        localStorage.setItem(`customer_phone_${restaurantId}`, digits);
      } catch { /* private mode — the webhook still activates Prime */ }
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-md mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50 text-sm mb-4 hover:text-white transition">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center gap-2 mb-5">
          <Crown className="w-6 h-6 text-yellow-400" />
          <span className="text-yellow-400 font-black tracking-widest uppercase text-sm">KAISO PRIME</span>
          {info?.restaurant_name && <span className="text-white/40 text-xs">· {info.restaurant_name}</span>}
        </div>

        {step === "info" && (
          <>
            <div className="mb-5">
              <h1 className="text-3xl font-black text-white leading-tight mb-2">
                Paga <span className="text-yellow-400">{fmtPrice(planPrice)} €</span><br />
                <span className="text-white/80">y desbloquea 7 € al instante.</span>
              </h1>
              <p className="text-white/60 text-sm">
                En cuanto se confirma el pago recibes un cupón de 7 € para tu próximo pedido,
                además del resto de ventajas PRIME.
              </p>
            </div>

            <div className="space-y-2 mb-5">
              {benefits.map(b => (
                <div key={b.title} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                  <span className="text-lg shrink-0">{b.icon}</span>
                  <div>
                    <p className="text-white text-sm font-semibold">{b.title}</p>
                    <p className="text-white/50 text-xs">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/5 rounded-2xl p-4 mb-5 text-center border border-white/10">
              <p className="text-white/50 text-xs mb-1">KAISO PRIME por</p>
              <p className="text-5xl font-black text-white">{fmtPrice(planPrice)} €</p>
              <p className="text-white/50 text-sm">/mes · cancela cuando quieras</p>
              <p className="text-white/30 text-xs mt-1">Sin compromiso ni permanencia.</p>
            </div>

            {gateways.length === 0 && (
              <p className="text-red-300 text-xs text-center mb-3">
                Este restaurante todavía no tiene una pasarela de pago configurada para PRIME.
              </p>
            )}
            <Button
              onClick={() => setStep("form")}
              disabled={gateways.length === 0}
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base h-12 rounded-xl shadow-lg disabled:opacity-50"
            >
              Quiero mi cupón de 7 €
            </Button>
          </>
        )}

        {step === "form" && (
          <div className="space-y-3">
            <h2 className="text-xl font-black text-white mb-2">Activar KAISO PRIME</h2>
            <div>
              <label className="text-white/70 text-xs font-medium block mb-1">Tu nombre</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nombre (opcional)"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-yellow-400 text-sm"
              />
            </div>
            <div>
              <label className="text-white/70 text-xs font-medium block mb-1">Número de teléfono *</label>
              <input
                type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 612 345 678"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-yellow-400 text-sm"
              />
              <p className="text-white/40 text-xs mt-1">El mismo número que usas en tus pedidos</p>
            </div>

            {gateways.length > 1 && (
              <div>
                <label className="text-white/70 text-xs font-medium block mb-1">Método de pago</label>
                <div className="flex gap-2">
                  {gateways.map(g => (
                    <button
                      key={g} type="button" onClick={() => setGateway(g)}
                      data-testid={`prime-page-gateway-${g}`}
                      className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-bold transition ${
                        gateway === g
                          ? "border-yellow-400 bg-yellow-400 text-black"
                          : "border-white/20 bg-white/10 text-white/80"
                      }`}
                    >
                      {GATEWAY_LABEL[g] || g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="text-red-400 text-xs">{error}</p>}
            <Button
              onClick={startCheckout} disabled={loading}
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-12 rounded-xl"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirigiendo al pago…</>
                : `Pagar ${fmtPrice(planPrice)} € y activar`}
            </Button>
            <button onClick={() => setStep("info")} className="w-full text-white/40 text-xs hover:text-white/70 py-1">
              Volver
            </button>
            <div className="flex items-center gap-1.5 text-white/30 text-xs justify-center">
              <Lock className="w-3 h-3" /> PRIME se activa solo tras confirmarse el pago
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
